import { computed, ref } from 'vue'
import type { AxiosResponse } from 'axios'
import { useRoute } from 'vue-router'
import type { AddItinerarySuccessResponse } from '../types/api/response/itinerary'
import type { SearchTravelModes } from '../types'
import type { AddCuratedTravelItineraryPayload } from '../types/api/payload/curatedTravel'
import useCuratedTravelStore from '../store/curatedTravel'
import {
  useAddCuratedTravelItineraryMutation,
  useAddItineraryMutation,
  useUpdateCuratedTravelItineraryMutation,
  useUpdateItineraryMutation,
} from '@/modules/Search/requests/mutations'
import type { AddItineraryPayload } from '@/modules/Search/types/api/payload/itinerary'
import useItinerarySummary from '@/modules/Search/store/itinerarySummary'
import useSearchPanelStore from '@/modules/Search/store/searchPanel'
import useAuthStore from '@/modules/Auth/store'
import { useItineraryQuery } from '@/modules/Search/requests/queries'
import useSearchPanelHistoryStore from '@/modules/Search/store/searchPanelHistory'
import useCurrencyStore from '@/modules/Core/store/currency'

function useItinerary() {
  const itinerarySummaryStore = useItinerarySummary()
  const authStore = useAuthStore()
  const searchPanelStore = useSearchPanelStore()
  const curatedTravelStore = useCuratedTravelStore()
  const searchPanelHistoryStore = useSearchPanelHistoryStore()

  const { mutateAsync: mutateAddItinerary, data: addItineraryData } =
    useAddItineraryMutation({
      meta: {
        noSuccessToast: true,
      },
      onSuccess: () => {
        itinerarySummaryStore.captureSearchType()
      },
    })
  const { mutateAsync: mutateUpdateItinerary } = useUpdateItineraryMutation({
    meta: {
      noSuccessToast: true,
    },
  })

  const route = useRoute()

  const getCurrentState: () => AddItineraryPayload = () => {
    if (
      !searchPanelStore.searchId &&
      searchPanelHistoryStore.history.searchId
    ) {
      searchPanelStore.searchId = searchPanelHistoryStore.history.searchId
    }

    return {
      searchId: searchPanelStore.searchId || '',
      ...(authStore.loggedIn
        ? { userId: searchPanelStore.getId }
        : { guestId: searchPanelStore.getId }),
      searchOptionAir: [],
      searchOptionHotel: [],
      searchOptionCar: [],
    }
  }

  const payload = ref<AddItineraryPayload>(getCurrentState())

  async function submitItinerary({ ignoreLoading = false } = {}) {
    payload.value = getCurrentState()

    curatedTravelStore.$reset()

    if (!ignoreLoading) {
      itinerarySummaryStore.isLoading = true
    }

    let response: AxiosResponse<AddItinerarySuccessResponse, any>

    if (itinerarySummaryStore.id) {
      response = await updateItinerary()
    } else {
      response = await addItinerary()
    }

    if (!ignoreLoading) {
      itinerarySummaryStore.isLoading = false
    }

    return response
  }

  async function addItinerary() {
    const { cars, flights, hotels } = itinerarySummaryStore.itemsSearchOptions

    payload.value.searchOptionAir = flights
    payload.value.searchOptionHotel = hotels
    payload.value.searchOptionCar = cars

    const response = await mutateAddItinerary(payload.value, {
      onError(error, { searchOptionAir, searchOptionCar, searchOptionHotel }) {
        onError({
          searchOptionAir,
          searchOptionHotel,
          searchOptionCar,
        })

        return error
      },
    })

    if (addItineraryData.value?.data) {
      itinerarySummaryStore.id = addItineraryData.value.data.data.id
    }

    return response
  }

  async function updateItinerary() {
    const { cars, flights, hotels } = itinerarySummaryStore.itemsSearchOptions

    payload.value.searchOptionAir = flights
    payload.value.searchOptionHotel = hotels
    payload.value.searchOptionCar = cars
    delete payload.value.guestId

    return await mutateUpdateItinerary(payload.value, {
      onError(error, { searchOptionAir, searchOptionCar, searchOptionHotel }) {
        onError({
          searchOptionAir,
          searchOptionHotel,
          searchOptionCar,
        })

        return error
      },
    })
  }

  // CURATED ITINERARY
  const {
    mutateAsync: mutateAddCuratedTravelItinerary,
    data: addCuratedTravelItineraryData,
  } = useAddCuratedTravelItineraryMutation({
    meta: {
      noSuccessToast: true,
    },
  })
  const { mutateAsync: mutateUpdateCuratedTravelItinerary } =
    useUpdateCuratedTravelItineraryMutation({
      meta: {
        noSuccessToast: true,
      },
    })

  const currency = useCurrencyStore()
  const currencyTitle = computed(() => currency.currency.title)

  const getCuratedCurrentState: () => AddCuratedTravelItineraryPayload = () => {
    return {
      searchId: curatedTravelStore.searchId ?? '',
      ...(authStore.loggedIn
        ? { userId: searchPanelStore.getId }
        : { guestId: searchPanelStore.getId }),
      tier: curatedTravelStore.tierType ?? '',
      curatedId: curatedTravelStore.curatedId ?? '',
      currency: currencyTitle.value,
      searchOptionAir: [],
      searchOptionCar: [],
      searchOptionHotel: [],
    }
  }

  const curatedPayload = ref<AddCuratedTravelItineraryPayload>(
    getCuratedCurrentState()
  )

  async function submitCuratedItinerary({
    tier,
    ignoreLoading = false,
  }: {
    tier?: string
    ignoreLoading?: boolean
  } = {}) {
    curatedPayload.value = getCuratedCurrentState()
    // FIXME: THIS IS TOTALLY SHITTY, WE NEED TO FIX THIS
    searchPanelStore.searchId = curatedTravelStore.searchId ?? ''
    if (tier) {
      curatedPayload.value.tier = tier
    }

    if (!ignoreLoading) {
      itinerarySummaryStore.isLoading = true
    }

    let response: AxiosResponse<AddItinerarySuccessResponse, any>

    if (itinerarySummaryStore.id) {
      response = await updateCuratedTravelItinerary()
    } else {
      response = await addCuratedTravelItinerary()
    }

    if (!ignoreLoading) {
      itinerarySummaryStore.isLoading = false
    }

    return response
  }

  async function addCuratedTravelItinerary() {
    const { cars, flights, hotels } = itinerarySummaryStore.itemsSearchOptions

    curatedPayload.value.searchOptionAir = flights
    curatedPayload.value.searchOptionHotel = hotels
    curatedPayload.value.searchOptionCar = cars

    const response = await mutateAddCuratedTravelItinerary(
      curatedPayload.value,
      {
        onError(
          error,
          { searchOptionAir, searchOptionCar, searchOptionHotel }
        ) {
          onError({
            searchOptionAir,
            searchOptionHotel,
            searchOptionCar,
          })

          return error
        },
      }
    )

    if (addCuratedTravelItineraryData.value?.data) {
      itinerarySummaryStore.id =
        addCuratedTravelItineraryData.value.data.data.id
    }

    return response
  }

  async function updateCuratedTravelItinerary() {
    const { cars, flights, hotels } = itinerarySummaryStore.itemsSearchOptions

    curatedPayload.value.searchOptionAir = flights
    curatedPayload.value.searchOptionHotel = hotels
    curatedPayload.value.searchOptionCar = cars
    delete curatedPayload.value.guestId

    return await mutateUpdateCuratedTravelItinerary(curatedPayload.value, {
      onError(error, { searchOptionAir, searchOptionCar, searchOptionHotel }) {
        onError({
          searchOptionAir,
          searchOptionHotel,
          searchOptionCar,
        })

        return error
      },
    })
  }

  function onError({
    searchOptionAir,
    searchOptionHotel,
    searchOptionCar,
  }: {
    searchOptionAir?: AddItineraryPayload['searchOptionAir']
    searchOptionHotel?: AddItineraryPayload['searchOptionHotel']
    searchOptionCar?: AddItineraryPayload['searchOptionCar']
  }) {
    itinerarySummaryStore.isLoading = false

    const searchOptions: {
      [key in SearchTravelModes]:
        | number[]
        | AddItineraryPayload['searchOptionHotel']
        | undefined
    } = {
      flight: searchOptionAir,
      hotel: searchOptionHotel,
      car: searchOptionCar,
    }

    Object.keys(searchOptions).forEach((key) => {
      const searchOption = searchOptions[key as SearchTravelModes]

      if (!searchOption) {
        return
      }

      searchOption.forEach((id) => {
        const searchOption = typeof id === 'number' ? id : id.searchOption
        const item = itinerarySummaryStore.getItemBySearchOption(
          searchOption,
          key as SearchTravelModes
        )

        if (!item) {
          return
        }

        itinerarySummaryStore.remove(item.uniqueId)
      })
    })
  }

  const searchId = computed(() => searchPanelStore.searchId ?? '')
  const itineraryId = computed(() => itinerarySummaryStore.id ?? '')

  const enabled = computed(
    () => !!itineraryId.value.length && !!searchId.value.length
  )

  const isCuratedTravelFlow = computed(
    () => !!(curatedTravelStore.searchId && curatedTravelStore.curatedId)
  )

  const searchIdValue = computed(() => {
    return isCuratedTravelFlow.value
      ? curatedTravelStore.searchId ?? ''
      : searchId.value
  })

  function fetchItinerary() {
    return useItineraryQuery(searchIdValue, itineraryId, {
      enabled,
      retry(_, error) {
        const message = (
          error as AxiosCustomError
        ).response?.data?.errors?.[0]?.propertyName?.toLowerCase()
        if (
          message?.includes('expired') ||
          message?.includes('search does not exist')
        ) {
          itinerarySummaryStore.id = null

          if (route.path === '/book') {
            itinerarySummaryStore.setItineraryHasError(true)
          }

          return false
        }
        return true
      },
    })
  }

  return {
    submitItinerary,
    submitCuratedItinerary,
    fetchItinerary,
    isCuratedTravelFlow,
    enabled,
  }
}

export default useItinerary
